import { useState, useRef, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, Send, Bot, User, Loader2, RotateCcw, CheckCircle2, ArrowRight, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ManagedCampaignData, BusinessDNA, CampaignGoalType } from "@/components/managed-campaign/ManagedCampaignData";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface AiCampaignStrategistProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "audience" | "messaging";
  currentData: ManagedCampaignData;
  onFieldUpdate: (field: string, value: any) => void;
  workspaceId: string;
  campaignId?: string;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  options?: UIOption[];
  field_to_update?: string;
  suggested_value?: any;
  phase?: string;
  allow_multi_select?: boolean;
  is_baseline?: boolean;
}

interface UIOption {
  label: string;
  value: string;
}

interface AIResponse {
  thought_process: string;
  bot_message: string;
  ui_suggestions: string[];
  field_to_update: string;
  suggested_value: any;
  requires_user_input: boolean;
  phase: string;
  progress_percent: number;
  allow_multi_select?: boolean;
  is_baseline?: boolean;
}

const PHASE_LABELS: Record<string, string> = {
  gatekeeper: "Campaign Goal",
  business_dna: "Business Context",
  baseline_generation: "Recommendations",
  audience: "Target Audience",
  messaging: "Messaging Strategy",
  recommendations: "Analysis"
};

export const AiCampaignStrategist = ({
  open,
  onOpenChange,
  mode,
  currentData,
  onFieldUpdate,
  workspaceId,
  campaignId
}: AiCampaignStrategistProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState<string>("gatekeeper");
  const [selectedMultiOptions, setSelectedMultiOptions] = useState<Set<string>>(new Set());
  const [activeMultiSelectMessageId, setActiveMultiSelectMessageId] = useState<string | null>(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const hasLoadedConversation = useRef(false);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  // Load existing conversation when dialog opens
  useEffect(() => {
    if (open && !hasLoadedConversation.current) {
      loadExistingConversation();
    }
  }, [open]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      hasLoadedConversation.current = false;
    }
  }, [open]);

  const loadExistingConversation = async () => {
    setIsLoadingConversation(true);
    hasLoadedConversation.current = true;

    try {
      const { data, error } = await supabase.functions.invoke("ai-campaign-strategist", {
        body: {
          action: "fetch",
          mode,
          workspaceId,
          campaignId
        }
      });

      if (error) throw error;

      if (data?.messages?.length > 0) {
        // Reconstruct messages - for the LAST assistant message, try to regenerate UI
        const reconstructedMessages: Message[] = data.messages.map((m: any, idx: number) => {
          const isLastAssistant = m.role === "assistant" && idx === data.messages.length - 1;
          
          // Try to detect if this was a baseline message
          const isBaseline = m.content.includes("Apply All") || 
                            m.content.includes("recommended target audience") ||
                            m.content.includes("**Industries:**");
          
          // For the last assistant message, preserve options for re-display
          if (isLastAssistant) {
            // Parse suggested values from markdown if it's a baseline
            let suggestedValue: any = null;
            if (isBaseline) {
              // Try to extract structured data from the message
              suggestedValue = extractBaselineFromMessage(m.content);
            }

            return {
              id: `loaded-${idx}`,
              role: m.role,
              content: m.content,
              options: generateOptionsFromContent(m.content, isBaseline),
              field_to_update: isBaseline ? "baseline" : undefined,
              suggested_value: suggestedValue,
              is_baseline: isBaseline
            };
          }
          
          return {
            id: `loaded-${idx}`,
            role: m.role,
            content: m.content,
            options: undefined,
            field_to_update: undefined,
            suggested_value: undefined
          };
        });

        setMessages(reconstructedMessages);
        
        // Set progress based on last message phase
        const lastAssistant = reconstructedMessages.filter(m => m.role === "assistant").pop();
        if (lastAssistant?.is_baseline) {
          setProgress(50);
          setCurrentPhase("baseline_generation");
        }
        
        toast.success("Conversation restored");
      } else {
        // No existing conversation, initialize fresh
        initializeConversation();
      }
    } catch (error) {
      console.error("Failed to load conversation:", error);
      // Fall back to initializing new conversation
      initializeConversation();
    } finally {
      setIsLoadingConversation(false);
    }
  };

  // Helper to extract baseline values from markdown message
  const extractBaselineFromMessage = (content: string): any => {
    try {
      const baseline: any = {};
      
      // Extract countries
      const countriesMatch = content.match(/\*\*Countries:\*\*\s*([^\n]+)/);
      if (countriesMatch) {
        baseline.countries = countriesMatch[1].split(",").map((s: string) => s.trim()).filter(Boolean);
      }
      
      // Extract industries
      const industriesMatch = content.match(/\*\*Industries:\*\*\s*([^\n]+)/);
      if (industriesMatch) {
        baseline.industries = industriesMatch[1].split(",").map((s: string) => s.trim()).filter(Boolean);
      }
      
      // Extract company sizes
      const sizesMatch = content.match(/\*\*Company Size:\*\*\s*([^\n]+)/);
      if (sizesMatch) {
        baseline.companySizes = sizesMatch[1].split(",").map((s: string) => s.trim()).filter(Boolean);
      }
      
      // Extract seniority
      const seniorityMatch = content.match(/\*\*Seniority:\*\*\s*([^\n]+)/);
      if (seniorityMatch) {
        baseline.seniorityLevels = seniorityMatch[1].split(",").map((s: string) => s.trim()).filter(Boolean);
      }
      
      // Extract job titles
      const titlesMatch = content.match(/\*\*Job Titles:\*\*\s*([^\n]+)/);
      if (titlesMatch) {
        baseline.jobTitles = titlesMatch[1].split(",").map((s: string) => s.trim()).filter(Boolean);
      }
      
      return Object.keys(baseline).length > 0 ? baseline : null;
    } catch {
      return null;
    }
  };

  // Helper to generate options from message content
  const generateOptionsFromContent = (content: string, isBaseline: boolean): UIOption[] => {
    if (isBaseline) {
      return [
        { label: "Apply All Recommendations", value: "apply_all" },
        { label: "Adjust industries", value: "adjust_industries" },
        { label: "Adjust job titles", value: "adjust_titles" },
        { label: "Start from scratch", value: "start_scratch" }
      ];
    }
    
    // For other messages, check for common patterns
    if (content.includes("primary goal")) {
      return [
        { label: "Generate Leads / Sales", value: "sales" },
        { label: "Recruiting / Hiring", value: "recruiting" },
        { label: "Brand Awareness", value: "branding" },
        { label: "Event Invitation", value: "events" }
      ];
    }
    
    return [];
  };

  const initializeConversation = async () => {
    setIsLoading(true);
    try {
      const response = await callAI(null);
      if (response) {
        addAssistantMessage(response);
      }
    } catch (error) {
      console.error("Failed to initialize:", error);
      toast.error("Failed to start AI assistant");
    } finally {
      setIsLoading(false);
    }
  };

  const callAI = async (userMessage: string | null, selectedOption?: UIOption): Promise<AIResponse | null> => {
    const currentContext = {
      campaignGoal: currentData.campaignGoal,
      businessDNA: currentData.businessDNA,
      managedCampaignData: {
        campaignName: currentData.campaignName,
        countries: currentData.countries,
        cities: currentData.cities,
        industries: currentData.industries,
        companySizes: currentData.companySizes,
        companyKeywords: currentData.companyKeywords,
        personKeywords: currentData.personKeywords,
        jobTitles: currentData.jobTitles,
        seniorityLevels: currentData.seniorityLevels,
        additionalNotes: currentData.additionalNotes
      },
      sequences: currentData.sequences
    };

    const { data, error } = await supabase.functions.invoke("ai-campaign-strategist", {
      body: {
        mode,
        workspaceId,
        campaignId,
        userMessage,
        selectedOption,
        currentContext,
        conversationHistory: messages.map(m => ({ role: m.role, content: m.content }))
      }
    });

    if (error) throw error;
    return data?.response;
  };

  const addAssistantMessage = (response: AIResponse) => {
    const options: UIOption[] = response.ui_suggestions?.map((label, i) => ({
      label,
      value: label.toLowerCase().replace(/[^a-z0-9]/g, "_")
    })) || [];

    const newMessage: Message = {
      id: Date.now().toString(),
      role: "assistant",
      content: response.bot_message,
      options,
      field_to_update: response.field_to_update,
      suggested_value: response.suggested_value,
      phase: response.phase,
      allow_multi_select: response.allow_multi_select,
      is_baseline: response.is_baseline
    };

    setMessages(prev => [...prev, newMessage]);
    setProgress(response.progress_percent || 0);
    setCurrentPhase(response.phase || "gatekeeper");

    // Reset multi-select state for new message
    setSelectedMultiOptions(new Set());
    if (response.allow_multi_select) {
      setActiveMultiSelectMessageId(newMessage.id);
    } else {
      setActiveMultiSelectMessageId(null);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      role: "user",
      content: input
    };

    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);

    try {
      const response = await callAI(input);
      if (response) {
        applyExtractedValue(response);
        addAssistantMessage(response);
      }
    } catch (err: any) {
      console.error("AI Error:", err);
      toast.error("AI Error: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptionClick = async (option: UIOption, message: Message) => {
    // Check if this is "Let me type my answer"
    if (option.value === "let_me_type_my_answer" || option.label.toLowerCase().includes("let me type")) {
      inputRef.current?.focus();
      return;
    }

    // Handle "Apply All Recommendations" for baseline
    if (option.value === "apply_all" || option.label.toLowerCase().includes("apply all")) {
      if (message.suggested_value && message.field_to_update === "baseline") {
        handleApplyBaseline(message.suggested_value);
      }
      // Continue conversation
      const userMsg: Message = {
        id: Date.now().toString(),
        role: "user",
        content: option.label
      };
      setMessages(prev => [...prev, userMsg]);
      setIsLoading(true);
      try {
        const response = await callAI(option.label, option);
        if (response) {
          addAssistantMessage(response);
        }
      } catch (err: any) {
        console.error("AI Error:", err);
        toast.error("AI Error: " + err.message);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Handle multi-select toggle
    if (message.allow_multi_select && activeMultiSelectMessageId === message.id) {
      const newSelected = new Set(selectedMultiOptions);
      if (newSelected.has(option.label)) {
        newSelected.delete(option.label);
      } else {
        newSelected.add(option.label);
      }
      setSelectedMultiOptions(newSelected);
      return;
    }

    // Single-select behavior
    const userMsg: Message = {
      id: Date.now().toString(),
      role: "user",
      content: option.label
    };

    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    try {
      if (message.field_to_update && message.field_to_update !== "none" && message.field_to_update !== "baseline") {
        applyFieldUpdate(message.field_to_update, option.label);
      }

      const response = await callAI(option.label, option);
      if (response) {
        applyExtractedValue(response);
        addAssistantMessage(response);
      }
    } catch (err: any) {
      console.error("AI Error:", err);
      toast.error("AI Error: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMultiSelectSubmit = async (message: Message) => {
    if (selectedMultiOptions.size === 0) {
      toast.error("Please select at least one option");
      return;
    }

    const selectedArray = Array.from(selectedMultiOptions);
    const userMsg: Message = {
      id: Date.now().toString(),
      role: "user",
      content: selectedArray.join(", ")
    };

    setMessages(prev => [...prev, userMsg]);
    setActiveMultiSelectMessageId(null);
    setIsLoading(true);

    try {
      if (message.field_to_update && message.field_to_update !== "none") {
        applyFieldUpdate(message.field_to_update, selectedArray);
      }

      const response = await callAI(selectedArray.join(", "));
      if (response) {
        applyExtractedValue(response);
        addAssistantMessage(response);
      }
    } catch (err: any) {
      console.error("AI Error:", err);
      toast.error("AI Error: " + err.message);
    } finally {
      setIsLoading(false);
      setSelectedMultiOptions(new Set());
    }
  };

  const applyExtractedValue = (response: AIResponse) => {
    if (!response.field_to_update || response.field_to_update === "none" || !response.suggested_value) {
      return;
    }
    
    // Handle baseline separately
    if (response.field_to_update === "baseline" && response.is_baseline) {
      // Don't auto-apply baseline, wait for user to click "Apply All"
      return;
    }
    
    applyFieldUpdate(response.field_to_update, response.suggested_value);
  };

  const handleApplyBaseline = (baseline: any) => {
    if (!baseline) return;
    
    // Apply all baseline fields at once
    if (baseline.countries?.length) {
      onFieldUpdate("countries", baseline.countries);
    }
    if (baseline.industries?.length) {
      onFieldUpdate("industries", baseline.industries);
    }
    if (baseline.companySizes?.length) {
      onFieldUpdate("companySizes", baseline.companySizes);
    }
    if (baseline.seniorityLevels?.length) {
      onFieldUpdate("seniorityLevels", baseline.seniorityLevels);
    }
    if (baseline.jobTitles?.length) {
      onFieldUpdate("jobTitles", baseline.jobTitles);
    }
    
    toast.success("Applied all recommendations to form!");
  };

  const applyFieldUpdate = (field: string, value: any) => {
    switch (field) {
      case "campaignGoal":
        const goalMap: Record<string, CampaignGoalType> = {
          "generate leads / sales": "sales",
          "generate leads": "sales",
          "sales": "sales",
          "recruiting / hiring": "recruiting",
          "recruiting": "recruiting",
          "hiring": "recruiting",
          "brand awareness": "branding",
          "branding": "branding",
          "event invitation": "events",
          "events": "events"
        };
        const normalizedGoal = goalMap[value.toLowerCase()] || "sales";
        onFieldUpdate("campaignGoal", normalizedGoal);
        break;

      case "businessDNA":
        if (typeof value === "string") {
          onFieldUpdate("businessDNA", { 
            ...currentData.businessDNA, 
            productService: value 
          });
        } else {
          onFieldUpdate("businessDNA", { ...currentData.businessDNA, ...value });
        }
        break;

      case "countries":
      case "industries":
      case "companySizes":
      case "jobTitles":
      case "seniorityLevels":
      case "companyKeywords":
      case "personKeywords":
        if (Array.isArray(value)) {
          onFieldUpdate(field, value);
        } else if (typeof value === "string") {
          const existing = currentData[field as keyof ManagedCampaignData] as string[] || [];
          if (!existing.includes(value)) {
            onFieldUpdate(field, [...existing, value]);
          }
        }
        break;

      case "sequences":
        if (Array.isArray(value)) {
          const existingSeqs = [...currentData.sequences];
          value.forEach((newSeq: any) => {
            const idx = existingSeqs.findIndex(s => s.step_number === newSeq.step_number);
            if (idx >= 0) {
              existingSeqs[idx] = { ...existingSeqs[idx], ...newSeq };
            }
          });
          onFieldUpdate("sequences", existingSeqs);
        }
        break;

      default:
        console.log("Unknown field:", field, value);
    }
  };

  const handleApplySuggestion = (message: Message) => {
    if (message.field_to_update === "baseline" && message.suggested_value) {
      handleApplyBaseline(message.suggested_value);
    } else if (message.field_to_update && message.suggested_value) {
      applyFieldUpdate(message.field_to_update, message.suggested_value);
      toast.success("Applied to form!");
    }
  };

  const resetChat = async () => {
    setMessages([]);
    setProgress(0);
    setCurrentPhase("gatekeeper");
    setSelectedMultiOptions(new Set());
    setActiveMultiSelectMessageId(null);
    hasLoadedConversation.current = true; // Prevent reload

    // Clear from database
    try {
      // We'll just reinitialize, the new conversation will overwrite
      toast.success("Conversation reset");
      setTimeout(() => initializeConversation(), 100);
    } catch (error) {
      console.error("Failed to reset:", error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Format suggested value for display
  const formatSuggestedValue = (value: any): string => {
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    if (typeof value === "object" && value !== null) {
      // For baseline objects, format nicely
      const parts: string[] = [];
      if (value.industries?.length) parts.push(`Industries: ${value.industries.join(", ")}`);
      if (value.companySizes?.length) parts.push(`Sizes: ${value.companySizes.join(", ")}`);
      if (value.seniorityLevels?.length) parts.push(`Seniority: ${value.seniorityLevels.join(", ")}`);
      if (value.jobTitles?.length) parts.push(`Titles: ${value.jobTitles.join(", ")}`);
      return parts.join(" • ");
    }
    return String(value);
  };

  // Render markdown-like content
  const renderContent = (content: string) => {
    // Simple markdown rendering for bold text
    const parts = content.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      // Handle newlines
      return part.split("\n").map((line, j) => (
        <span key={`${i}-${j}`}>
          {line}
          {j < part.split("\n").length - 1 && <br />}
        </span>
      ));
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] h-[650px] flex flex-col p-0 gap-0 overflow-hidden">
        {/* Header with progress */}
        <DialogHeader className="p-4 pb-3 border-b bg-gradient-to-r from-primary/5 to-primary/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg">
                <Sparkles className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold">AI Campaign Strategist</DialogTitle>
                <DialogDescription className="text-xs">
                  {mode === "audience" ? "Building your target audience" : "Crafting your messaging"}
                </DialogDescription>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Progress Ring */}
              <div className="flex items-center gap-2">
                <div className="relative w-10 h-10">
                  <svg className="w-10 h-10 transform -rotate-90">
                    <circle
                      cx="20"
                      cy="20"
                      r="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      className="text-muted"
                    />
                    <circle
                      cx="20"
                      cy="20"
                      r="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeDasharray={`${progress} 100`}
                      strokeLinecap="round"
                      className="text-primary transition-all duration-500"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold">
                    {progress}%
                  </span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {PHASE_LABELS[currentPhase] || currentPhase}
                </Badge>
              </div>

              {/* Reset button - more visible */}
              <Button
                variant="outline"
                size="sm"
                onClick={resetChat}
                className="h-8 gap-1.5 text-muted-foreground hover:text-destructive hover:border-destructive/50"
                title="Reset Conversation"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span className="text-xs">Reset</span>
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          {isLoadingConversation ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <span className="ml-2 text-muted-foreground">Loading conversation...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={cn(
                    "flex gap-3",
                    msg.role === "user" ? "justify-end" : "justify-start"
                  )}
                >
                  {msg.role === "assistant" && (
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                  )}

                  <div className={cn("flex flex-col gap-2 max-w-[85%]")}>
                    {/* Message bubble */}
                    <div
                      className={cn(
                        "rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground rounded-tr-sm"
                          : "bg-muted rounded-tl-sm"
                      )}
                    >
                      {msg.role === "assistant" ? renderContent(msg.content) : msg.content}
                    </div>

                    {/* Baseline recommendation card */}
                    {msg.role === "assistant" && 
                     msg.is_baseline && 
                     msg.suggested_value && (
                      <div className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/30 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-primary" />
                          <h4 className="font-semibold text-sm">Recommended Target Audience</h4>
                        </div>
                        
                        <div className="grid gap-1.5 text-sm text-muted-foreground">
                          {msg.suggested_value.countries?.length > 0 && (
                            <div><strong className="text-foreground">Countries:</strong> {msg.suggested_value.countries.join(", ")}</div>
                          )}
                          {msg.suggested_value.industries?.length > 0 && (
                            <div><strong className="text-foreground">Industries:</strong> {msg.suggested_value.industries.join(", ")}</div>
                          )}
                          {msg.suggested_value.companySizes?.length > 0 && (
                            <div><strong className="text-foreground">Company Size:</strong> {msg.suggested_value.companySizes.join(", ")}</div>
                          )}
                          {msg.suggested_value.seniorityLevels?.length > 0 && (
                            <div><strong className="text-foreground">Seniority:</strong> {msg.suggested_value.seniorityLevels.join(", ")}</div>
                          )}
                          {msg.suggested_value.jobTitles?.length > 0 && (
                            <div><strong className="text-foreground">Job Titles:</strong> {msg.suggested_value.jobTitles.join(", ")}</div>
                          )}
                        </div>
                        
                        <div className="flex gap-2 mt-4">
                          <Button 
                            onClick={() => handleApplyBaseline(msg.suggested_value)}
                            className="gap-1.5"
                            size="sm"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            Apply All Recommendations
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Suggested value preview with Apply All button (for non-baseline arrays) */}
                    {msg.role === "assistant" && 
                     msg.suggested_value && 
                     !msg.is_baseline &&
                     Array.isArray(msg.suggested_value) &&
                     msg.suggested_value.length > 0 &&
                     msg.field_to_update &&
                     msg.field_to_update !== "none" && (
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground mb-2">
                          <strong>Suggested:</strong> {formatSuggestedValue(msg.suggested_value)}
                        </p>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleApplySuggestion(msg)}
                          className="text-xs gap-1.5"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Apply All
                        </Button>
                      </div>
                    )}

                    {/* Multi-select options with checkboxes */}
                    {msg.role === "assistant" && 
                     msg.options && 
                     msg.options.length > 0 && 
                     msg.allow_multi_select && 
                     activeMultiSelectMessageId === msg.id && (
                      <div className="space-y-2 mt-1">
                        <p className="text-xs text-muted-foreground">Select all that apply:</p>
                        <div className="flex flex-wrap gap-2">
                          {msg.options.map((option, i) => {
                            const isLetMeType = option.label.toLowerCase().includes("let me type");
                            const isSelected = selectedMultiOptions.has(option.label);
                            
                            if (isLetMeType) {
                              return (
                                <Button
                                  key={i}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOptionClick(option, msg)}
                                  disabled={isLoading}
                                  className="text-xs h-8 px-3 border-dashed"
                                >
                                  {option.label}
                                </Button>
                              );
                            }
                            
                            return (
                              <Button
                                key={i}
                                variant={isSelected ? "default" : "outline"}
                                size="sm"
                                onClick={() => handleOptionClick(option, msg)}
                                disabled={isLoading}
                                className={cn(
                                  "text-xs h-8 px-3 transition-colors gap-1.5",
                                  isSelected && "ring-2 ring-primary/30"
                                )}
                              >
                                <Checkbox 
                                  checked={isSelected} 
                                  className="w-3.5 h-3.5 pointer-events-none"
                                />
                                {option.label}
                              </Button>
                            );
                          })}
                        </div>
                        {selectedMultiOptions.size > 0 && (
                          <Button
                            onClick={() => handleMultiSelectSubmit(msg)}
                            disabled={isLoading}
                            size="sm"
                            className="mt-2 gap-1.5"
                          >
                            <ArrowRight className="w-3.5 h-3.5" />
                            Continue with {selectedMultiOptions.size} selected
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Single-select clickable options */}
                    {msg.role === "assistant" && 
                     msg.options && 
                     msg.options.length > 0 && 
                     !msg.is_baseline &&
                     (!msg.allow_multi_select || activeMultiSelectMessageId !== msg.id) && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {msg.options.map((option, i) => (
                          <Button
                            key={i}
                            variant="outline"
                            size="sm"
                            onClick={() => handleOptionClick(option, msg)}
                            disabled={isLoading}
                            className={cn(
                              "text-xs h-8 px-3 hover:bg-primary hover:text-primary-foreground transition-colors",
                              option.label.toLowerCase().includes("let me type") && "border-dashed",
                              option.label.toLowerCase().includes("apply all") && "bg-primary/10 border-primary/30 hover:bg-primary"
                            )}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    )}

                    {/* Options for baseline messages */}
                    {msg.role === "assistant" && 
                     msg.is_baseline && 
                     msg.options && 
                     msg.options.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {msg.options.filter(o => !o.label.toLowerCase().includes("apply all")).map((option, i) => (
                          <Button
                            key={i}
                            variant="outline"
                            size="sm"
                            onClick={() => handleOptionClick(option, msg)}
                            disabled={isLoading}
                            className="text-xs h-8 px-3 hover:bg-primary hover:text-primary-foreground transition-colors"
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    )}

                    {/* Apply suggestion button for non-array values */}
                    {msg.role === "assistant" && 
                     msg.suggested_value && 
                     !msg.is_baseline &&
                     !Array.isArray(msg.suggested_value) &&
                     msg.field_to_update && 
                     msg.field_to_update !== "none" && 
                     msg.field_to_update !== "baseline" &&
                     !msg.options?.length && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleApplySuggestion(msg)}
                        className="w-fit text-xs gap-1.5"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Apply to form
                      </Button>
                    )}
                  </div>

                  {msg.role === "user" && (
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0 mt-1">
                      <User className="w-4 h-4 text-secondary-foreground" />
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Loader2 className="w-4 h-4 text-primary animate-spin" />
                  </div>
                  <div className="bg-muted rounded-2xl rounded-tl-sm px-4 py-2.5">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}

              <div ref={scrollRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="p-4 border-t bg-background">
          <div className="flex gap-2">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your answer..."
              className="min-h-[44px] max-h-[120px] resize-none"
              disabled={isLoading || isLoadingConversation}
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isLoading || isLoadingConversation}
              size="icon"
              className="h-[44px] w-[44px] shrink-0"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
